import React, { useEffect, useRef } from "react";

const ITEM_HEIGHT = 40;
const VISIBLE = 3;

interface Range {
  label: string;
  mid: number;
}

// 3k steps up to 25k, 5k steps 25k–40k, 20k steps 40k–1L, then 1L–2L, above 2L
const RANGES: Range[] = [
  { label: "< ₹15,000",            mid: 15000  },
  { label: "₹15,000 - ₹18,000",   mid: 16500  },
  { label: "₹18,000 - ₹21,000",   mid: 19500  },
  { label: "₹21,000 - ₹24,000",   mid: 22500  },
  { label: "₹25,000 - ₹30,000",   mid: 27500  },
  { label: "₹30,000 - ₹35,000",   mid: 32500  },
  { label: "₹35,000 - ₹40,000",   mid: 37500  },
  { label: "₹40,000 - ₹60,000",   mid: 50000  },
  { label: "₹60,000 - ₹80,000",   mid: 70000  },
  { label: "₹80,000 - ₹1,00,000", mid: 90000  },
  { label: "₹1,00,000 - ₹2,00,000", mid: 150000 },
  { label: "Above ₹2,00,000",      mid: 200000 },
];

interface Props {
  value: number;       // midpoint of selected range
  onChange: (mid: number) => void;
}

export default function ScrollPicker({ value, onChange }: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const totalHeight = ITEM_HEIGHT * VISIBLE;
  const pad = ITEM_HEIGHT * Math.floor(VISIBLE / 2);

  useEffect(() => {
    const idx = RANGES.findIndex((r) => r.mid === value);
    const target = idx !== -1 ? idx : 0;
    if (listRef.current) {
      listRef.current.scrollTop = target * ITEM_HEIGHT;
    }
  }, []); // eslint-disable-line

  const handleScroll = () => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      if (!listRef.current) return;
      const idx = Math.round(listRef.current.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(idx, RANGES.length - 1));
      listRef.current.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: "smooth" });
      onChange(RANGES[clamped].mid);
    }, 100);
  };

  return (
    <div style={{
      position: "relative",
      height: totalHeight,
      borderRadius: 12,
      overflow: "hidden",
      background: "transparent",
      userSelect: "none",
    }}>
      {/* Top fade */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: pad,
        background: "linear-gradient(to bottom, rgba(255,242,238,0.94) 0%, rgba(255,242,238,0) 100%)",
        pointerEvents: "none", zIndex: 2,
      }} />

      {/* Bottom fade */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, height: pad,
        background: "linear-gradient(to top, rgba(255,242,238,0.94) 0%, rgba(255,242,238,0) 100%)",
        pointerEvents: "none", zIndex: 2,
      }} />

      {/* Highlight band behind center item */}
      <div style={{
        position: "absolute",
        top: pad,
        left: "15%",
        right: "15%",
        height: ITEM_HEIGHT,
        background: "#d32028",
        borderRadius: 8,
        zIndex: 1,
        pointerEvents: "none",
      }} />

      {/* Scroll list */}
      <div
        ref={listRef}
        onScroll={handleScroll}
        style={{
          height: "100%",
          overflowY: "scroll",
          scrollSnapType: "y mandatory",
          paddingTop: pad,
          paddingBottom: pad,
          scrollbarWidth: "none",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 3,
        }}
      >
        {RANGES.map((r) => {
          const isSelected = r.mid === value;
          return (
            <div
              key={r.mid}
              style={{
                height: ITEM_HEIGHT,
                scrollSnapAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isSelected ? "0.95rem" : "0.82rem",
                fontWeight: isSelected ? "700" : "400",
                color: isSelected ? "#fff" : "#aaa",
                letterSpacing: isSelected ? "0.01em" : "0",
                transition: "all 0.15s ease",
                fontFamily: "Outfit, sans-serif",
                cursor: "pointer",
              }}
              onClick={() => {
                onChange(r.mid);
                listRef.current?.scrollTo({
                  top: RANGES.indexOf(r) * ITEM_HEIGHT,
                  behavior: "smooth",
                });
              }}
            >
              {r.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}
